import {
  LinkSlackIdentityCommand,
  type LinkSlackIdentityCommandInput,
  type LinkSlackIdentityCommandOutput,
} from '@amzn/gbsa-orbit-typescript-client';
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { apiOrbitClient } from '../index';
import { sharedUserPreferencesKeys, v2UserPreferencesMutationKeys } from './keys';

/**
 * v2 React Query mutation for linking the user's alias to their Slack
 * identity via Orbit (which resolves the alias through SLAB).
 *
 * The operation is idempotent and server-managed: `slackIdentity` is never
 * writable through UpdateUserPreferences, so this is the only client path
 * that establishes it. Linking is inert until the user's preferences carry
 * `alertCenter.slack = true` — callers fire it on first Slack opt-in.
 *
 * On success the shared user-preferences read cache is invalidated so the
 * refreshed record carries the new `slackIdentity`, which the opt-in guards
 * (link-on-toggle, self-heal) read to decide whether a link is still needed.
 *
 * @param key - Identity of the preferences record. Used as the mutation key.
 * @param options - React Query overrides. `mutationKey` and `mutationFn`
 *                  are owned by the hook and cannot be supplied.
 */
export const useLinkSlackIdentityMutation = (
  key: Pick<LinkSlackIdentityCommandInput, 'programId' | 'userId'>,
  options?: Omit<
    UseMutationOptions<
      LinkSlackIdentityCommandOutput,
      Error,
      LinkSlackIdentityCommandInput
    >,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<
  LinkSlackIdentityCommandOutput,
  Error,
  LinkSlackIdentityCommandInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationKey: v2UserPreferencesMutationKeys.linkSlack(key),
    mutationFn: (data: LinkSlackIdentityCommandInput) =>
      apiOrbitClient.send(new LinkSlackIdentityCommand(data)),
    // React Query v5: onSuccess is (data, variables, onMutateResult, context).
    async onSuccess(data, variables, onMutateResult, context) {
      await queryClient.invalidateQueries({ queryKey: sharedUserPreferencesKeys.all });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
